import React, { FunctionComponent, RefObject, useCallback, useMemo, memo } from "react";
import { Grid, Box, GridItem } from "@chakra-ui/react";
import assert from "assert";
import times from "ramda/src/times";
import { isEqual } from "lodash";

import BatteryCard from "./cards/BatteryCard";
import ConverterCard from "./cards/ConverterCard";
import SuperchargerCard from "./cards/SuperchargerCard";
import SwitchCard from "./cards/SwitchCard";
import AtsCard from "./cards/AtsCard";
import PvCard from "./cards/PvCard";
import GridCard from "./cards/GridCard";
import LogoCard from "./cards/LogoCard";
import HVACCard from "./cards/HVACCard";
import CHPCard from "./cards/CHPCard";
import AnalyticsCard from "./cards/AnalyticsCard";
import WindTurbineCard from "./cards/WindTurbineCard";
import TransformerCard from "./cards/TransformerCard";
import FactoryCard from "./cards/FactoryCard";
import GensetCard from "./cards/GensetCard";
import FurnaceCard from "./cards/FurnaceCard";
import PaintShopCard from "./cards/PaintShopCard";
import HeatingCard from "./cards/HeatingCard";
import ExternalCommandCard from "./cards/ExternalCommandCard";
import RtuCommandCard from "./cards/RtuCommandCard";
import UnsupportedCard from "./cards/UnsupportedCard";
import {
  // DashboardMeasurement_dashboardMeasurement,
  SiteConfig_siteConfig_dashboardConf_components,
} from "graphql/generated";
import { useAllSitesData } from "common/all-sites-data";


interface IProps {
  siteId: string;
  gridWidth: number;
  gridHeight: number;
  // cardsConfig: Partial<Record<string, ICardConfig>>;
  components: Array<SiteConfig_siteConfig_dashboardConf_components>;
  containerRef: RefObject<HTMLDivElement>;
  data: Record<string, any>;
}


const Measurements: FunctionComponent<IProps> = memo(function Measurements({
  siteId,
  gridWidth,
  gridHeight,
  components,
  data,
  containerRef,
}) {
  const baseUrl = useMemo(() => `/site/${siteId}`, [siteId]);
  const { alarmsData } = useAllSitesData();
  const siteAlarmsData = useMemo<string[]>(() => alarmsData[siteId]?.alarms.active_ns ?? [], [alarmsData, siteId]);
  const alarmsLink = useCallback((alarmsNs: string) => siteAlarmsData.includes(alarmsNs) ? `${baseUrl}/alarms` : undefined, [siteAlarmsData, baseUrl]);

  const getCardData = useCallback((
    cardConfig: SiteConfig_siteConfig_dashboardConf_components,
    sumBucketName: string,
    stashBucketNames: string[] = [],
  ): {[index: string]: string | number | boolean | null} | undefined => {
    const bucketName = cardConfig.id !== null ? `${cardConfig.cardId}_dig` : sumBucketName;

    if (data) {
      stashBucketNames.forEach((bucket: string) => {
        if (data[bucketName]) data[bucketName][bucket] = data[bucket];
      });

      return data[bucketName];
    } 
  }, [data]);

  return (
    <Box position="relative">
      <Grid
        templateColumns={`repeat(${gridWidth}, minmax(min-content, 1fr))`}
        gap={20}
        ref={containerRef}
      >
        {times(
          (y) =>
            times((x) => {
              const cardInfo = Object.entries(components).find(
                ([id, config]) => config.x === x && config.y === y
              );
              const cardGridPosition = {x, y};

              if (cardInfo) {
                const [, cardConfig] = cardInfo;

                switch (cardConfig.type) {
                  case "battery":
                    return (
                      <BatteryCard
                        {...cardGridPosition}
                        data={getCardData(cardConfig, "battery", ["battery_control"])}
                        baseUrl={baseUrl}
                        config={cardConfig}
                        alarmsLink={alarmsLink("bess")}
                      />
                    );

                  case "converter":
                    return (
                      <ConverterCard
                        {...cardGridPosition}
                        data={getCardData(cardConfig, "converters_sum")}
                        baseUrl={baseUrl}
                        config={cardConfig}
                        alarmsLink={alarmsLink("converter")}
                      />
                    );

                  case "supercharger":
                    return (
                      <SuperchargerCard
                        {...cardGridPosition}
                        data={getCardData(cardConfig, "chargers_sum")}
                        baseUrl={baseUrl}
                        config={cardConfig}
                        alarmsLink={alarmsLink("charger")}
                      />
                    );

                  case "switch":
                    return (
                      <SwitchCard
                        {...cardGridPosition}
                        data={getCardData(cardConfig, "switch")}
                        config={cardConfig}
                        alarmsLink={alarmsLink("switch")}
                      />
                    );

                  case "ats":
                    return (
                      <AtsCard
                        {...cardGridPosition}
                        data={getCardData(cardConfig, "ats")}
                        config={cardConfig}
                        alarmsLink={alarmsLink("ats")}
                      />
                    );

                  case "pv":
                    return (
                      <PvCard
                        {...cardGridPosition}
                        baseUrl={baseUrl}
                        data={getCardData(cardConfig, "pv_sum", ["pv_control"])}
                        config={cardConfig}
                        alarmsLink={alarmsLink("pv")}
                      />
                    );

                  case "grid":
                    return (
                      <GridCard
                        {...cardGridPosition}
                        data={getCardData(cardConfig, "grid")}
                        baseUrl={baseUrl}
                        config={cardConfig}
                        alarmsLink={alarmsLink("grid")}
                      />
                    );

                  case "logo":
                    return (
                      <LogoCard
                        {...cardGridPosition}
                      />
                    );

                  case "chp":
                    return (
                      <CHPCard
                        {...cardGridPosition}
                        data={getCardData(cardConfig, "chp_sum", ["chp_control"])}
                        baseUrl={baseUrl}
                        config={cardConfig}
                        alarmsLink={alarmsLink("chp")}
                      />
                    );

                  case "hvac":
                    return (
                      <HVACCard
                        {...cardGridPosition}
                        data={getCardData(cardConfig, "hvac_sum")}
                        config={cardConfig}
                        alarmsLink={alarmsLink("hvac")}
                      />
                    );

                  case "analytics":
                    assert(
                      cardConfig.chartData,
                      `Missing "chartData" for "analytics" card`
                    );
                    return (
                      <AnalyticsCard
                        {...cardGridPosition} chart={cardConfig.chartData} />
                    );

                  case "wind_turbine":
                    return (
                      <WindTurbineCard
                        {...cardGridPosition}
                        data={getCardData(cardConfig, "wt_sum")}
                        config={cardConfig}
                        alarmsLink={alarmsLink("wt")}
                      />
                    );

                  case "transformer":
                    return (
                      <TransformerCard
                        {...cardGridPosition}
                        data={getCardData(cardConfig, "transformers_sum")}
                        config={cardConfig}
                        alarmsLink={alarmsLink("transformer")}
                      />
                    );

                  case "factory":
                    return (
                      <FactoryCard
                        {...cardGridPosition}
                        data={getCardData(cardConfig, "factory_sum")}
                        config={cardConfig}
                        alarmsLink={alarmsLink("factory")}
                      />
                    );

                  case "genset":
                    return (
                      <GensetCard
                        {...cardGridPosition}
                        data={getCardData(cardConfig, "gensets_sum")}
                        baseUrl={baseUrl}
                        config={cardConfig}
                        alarmsLink={alarmsLink("genset")}
                      />
                    );

                  case "furnace":
                    return (
                      <FurnaceCard
                        {...cardGridPosition}
                        data={getCardData(cardConfig, "furnaces_sum")}
                        baseUrl={baseUrl}
                        config={cardConfig}
                        alarmsLink={alarmsLink("furnace")}
                      />
                    );

                  case "paint_shop":
                    return (
                      <PaintShopCard
                        {...cardGridPosition}
                        data={getCardData(cardConfig, "ps_sum")}
                        baseUrl={baseUrl}
                        config={cardConfig}
                        alarmsLink={alarmsLink("ps")}
                      />
                    );

                  case "heating":
                    return (
                      <HeatingCard
                        {...cardGridPosition}
                        data={getCardData(cardConfig, "heating_sum", ["heating_control"])}
                        baseUrl={baseUrl}
                        config={cardConfig}
                        alarmsLink={alarmsLink("heating")}
                      />
                    );

                  case "external_command":
                    return (
                      <ExternalCommandCard
                        {...cardGridPosition}
                        data={getCardData(cardConfig, "external_command")}
                        baseUrl={baseUrl}
                        config={cardConfig}
                        alarmsLink={alarmsLink("external_command")}
                      />
                    );

                  case "rtu_command":
                    return (
                      <RtuCommandCard
                        {...cardGridPosition}
                        data={getCardData(cardConfig, "rtu_command")}
                        baseUrl={baseUrl}
                        config={cardConfig}
                        alarmsLink={alarmsLink("rtu_command")}
                      />
                    );

                  default:
                    return (
                      <UnsupportedCard
                        {...cardGridPosition}
                        config={cardConfig}
                      />
                    );
                }
              } else {
                return (
                  <GridItem w="100%" key={`${x}_${y}`} />
                )
              }
            }, gridWidth),
          gridHeight
        )}
      </Grid>
    </Box>
  );
}, isEqual);

export default Measurements;
